import { t } from "@hakili/i18n";
import { Button, Card, Field } from "@hakili/ui";
import { useState } from "react";

/**
 * `P0-CNW-001`, round A of dealer onboarding: **the console screen that opens a
 * dealership.**
 *
 * This is the narrow first slice of `/partenaires`, and `P3-CNW-003` still owns
 * the full screen , the directories, the portfolios, the onboarding entry.
 * What is here is the one gesture the dealer walk needs and nothing beside it.
 *
 * ## The two checkboxes are the product, not a formality
 *
 * 6.1.2 step 2 refuses to open an organisation unless the offline checks are
 * recorded: the person was MET and the convention was SIGNED. The API enforces
 * it and answers with a sentence saying so. They are rendered as two separate
 * confirmations rather than one, because they are two different acts on two
 * different days, and a single box would let one be ticked for the other.
 *
 * **`Dealers never self-register`** is the architect's ruling, and the intro
 * line says it on the screen. A dealership is opened by Hakili, in person,
 * after a signature , the screen should not read like a sign-up form.
 */

export interface PartenairesProps {
  /** Hands the opened dealership up, so the fiche can name its administrator. */
  readonly onOuverte?: (concession: { id: string; nom: string }) => void;
  readonly ouvrirConcession: (saisie: {
    nom: string;
    identiteVerifiee: boolean;
    conventionRecue: boolean;
  }) => Promise<{ ok: true; id: string } | { ok: false; message: string }>;
}

export function Partenaires({ ouvrirConcession, onOuverte }: PartenairesProps): React.JSX.Element {
  const [nom, setNom] = useState("");
  const [identiteVerifiee, setIdentiteVerifiee] = useState(false);
  const [conventionRecue, setConventionRecue] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);

  const soumettre = async (): Promise<void> => {
    setErreur(null);
    setOuverte(null);
    if (nom.trim() === "") {
      setErreur(t("console.concession.erreurNom"));
      return;
    }
    // The guard against a second click while the first is in flight. Two
    // dealerships created by an impatient double-click is a real outcome, and
    // the API has no idempotency key on this route.
    if (enCours) return;
    setEnCours(true);
    const resultat = await ouvrirConcession({ nom: nom.trim(), identiteVerifiee, conventionRecue });
    setEnCours(false);
    if (resultat.ok) {
      setOuverte(resultat.id);
      onOuverte?.({ id: resultat.id, nom: nom.trim() });
      // Cleared so a second dealership cannot be opened by a stray second
      // click carrying the first one's confirmations.
      setNom("");
      setIdentiteVerifiee(false);
      setConventionRecue(false);
      return;
    }
    setErreur(resultat.message);
  };

  return (
    <Card>
      {/* `Card` takes no `title`, so the heading is the screen's own. The kit is
          deliberately narrow and widening it for one screen would be the wrong
          direction , `P0-UIK`'s components are shared by six applications. */}
      <h1 className="text-lg font-medium text-gray-900">{t("console.concession.titre")}</h1>
      <p className="mb-4 mt-1 text-sm text-gray-500">{t("console.concession.intro")}</p>

      <Field label={t("console.concession.nom")} htmlFor="concession-nom">
        <input
          id="concession-nom"
          value={nom}
          onChange={(event) => {
            setNom(event.target.value);
          }}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          data-testid="concession-nom"
        />
      </Field>

      <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={identiteVerifiee}
          onChange={(event) => {
            setIdentiteVerifiee(event.target.checked);
          }}
          data-testid="concession-identite"
        />
        <span>{t("console.concession.identiteVerifiee")}</span>
      </label>

      <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={conventionRecue}
          onChange={(event) => {
            setConventionRecue(event.target.checked);
          }}
          data-testid="concession-convention"
        />
        <span>{t("console.concession.conventionRecue")}</span>
      </label>

      <div className="mt-4">
        {/* **The kit's `Button` has no `disabled`, and `inactive` is documented
            as CLIENT surfaces only.** So the double-submit guard is in the
            handler rather than in the markup , which is the more reliable place
            anyway: a disabled attribute set after an await has already lost the
            race it was meant to win. */}
        <Button
          onClick={() => {
            void soumettre();
          }}
        >
          {t("console.concession.ouvrir")}
        </Button>
      </div>

      {erreur === null ? null : (
        <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="concession-erreur">
          {erreur}
        </p>
      )}

      {ouverte === null ? null : (
        // `role="status"` and not `alert`: this is a success, and a screen
        // reader should hear it without the interruption an alert carries.
        <p role="status" className="mt-3 text-sm text-gray-700" data-testid="concession-ouverte">
          {t("console.concession.ouverte")} {t("console.concession.prochaineEtape")}
        </p>
      )}
    </Card>
  );
}
